/**
 * AR-3 Outside Pressure Index Algorithm
 * Method ID: b1-pressure-index-comprehensive
 *
 * Evaluates turn pressure transfer quality by analyzing the ratio of
 * pressure borne by the outside ski during a turn, the fluctuation of
 * that ratio, its stability around the ideal 0.75 target, and how
 * quickly the pressure transfer completes after turn entry.
 *
 * 评估转弯压力转移质量：分析弯内外板（外侧雪板）压力占比、占比波动、
 * 围绕理想值 0.75 的稳定性，以及入弯后压力转移完成的速度。
 *
 * Deterministic per PO-5.4 (no random numbers).
 * 按 PO-5.4 确定性实现（无随机数）。
 */

import { AR3_PARAMS } from '../constants/algorithm-params';
import type { SkiTickData } from '../types/ski-data';

// ===== Output Interface =====

/** AR-3 外侧压感指数结果 / Outside pressure index result for a single turn */
export interface OutsidePressureResult {
  score: number;                    // 0-100 综合评分
  confidence: number;               // 0.85
  outside_ratio_peak: number;       // peak outside foot ratio / 外板占比峰值
  outside_ratio_avg: number;        // average outside foot ratio / 外板占比均值
  outside_ratio_series: number[];   // per-frame ratio / 每帧外板占比
  fluctuation: number;              // std dev of ratio / 占比标准差
  stability_score: number;          // 0-100
  ratio_score: number;              // 0-100
  fluctuation_score: number;        // 0-100
  transfer_speed_score: number;     // 0-100
  transfer_time_ms: number;         // 0 if no transfer detected / 无转移时为 0
  outside_foot: 'left' | 'right' | 'neutral';
  entry_ms: number | null;          // 入弯点时间戳 / null if not found
  exit_ms: number | null;           // 出弯点时间戳 / null if not found
}

// ===== Helper Functions =====

/**
 * Determine which foot is the outside foot based on gyroscope Z.
 * - gyroZ > outsideThreshold  → 'right' (right foot is outside)
 * - gyroZ < -outsideThreshold → 'left'  (left foot is outside)
 * - otherwise                 → 'neutral'
 *
 * 根据陀螺仪 Z 判定外板。
 *
 * @param gyroZ - gyroscope Z value (rad/s) / 陀螺仪 Z 值（弧度/秒）
 * @returns outside foot label / 外板标签
 */
export function determineOutsideFoot(
  gyroZ: number
): 'left' | 'right' | 'neutral' {
  const threshold = AR3_PARAMS.outsideThreshold; // 0.1
  if (gyroZ > threshold) return 'right';
  if (gyroZ < -threshold) return 'left';
  return 'neutral';
}

/**
 * Compute the outside-foot pressure ratio for a single frame.
 *
 * total = pressure_left + pressure_right
 * - total == 0           → 0.5 (no pressure, balanced)
 * - gyroZ > 0.1          → pressure_right / total  (right is outside)
 * - gyroZ < -0.1         → pressure_left  / total  (left is outside)
 * - otherwise            → 0.5
 *
 * 计算单帧外板压力占比。
 *
 * @param pressureLeft - normalized left foot pressure 0-1
 * @param pressureRight - normalized right foot pressure 0-1
 * @param gyroZ - gyroscope Z value (rad/s) / 陀螺仪 Z 值
 * @returns outside foot pressure ratio in [0, 1] / 外板压力占比
 */
export function computeRatio(
  pressureLeft: number,
  pressureRight: number,
  gyroZ: number
): number {
  const total = pressureLeft + pressureRight;
  if (total === 0) return 0.5;

  const threshold = AR3_PARAMS.outsideThreshold; // 0.1
  if (gyroZ > threshold) {
    // Right foot is outside / 右脚为外板
    return pressureRight / total;
  }
  if (gyroZ < -threshold) {
    // Left foot is outside / 左脚为外板
    return pressureLeft / total;
  }
  // Ambiguous direction → neutral 0.5 / 方向不明确 → 中性 0.5
  return 0.5;
}

/**
 * Find the entry and exit frame indices for the pressure transfer phase.
 *
 * - Entry: first frame in [start, end] where |gyro.z| > entryThreshold (0.3)
 * - Exit:  first frame after entry (still within [start, end]) where
 *          |gyro.z| < exitThreshold (0.15)
 *
 * 查找压力转移阶段的入弯点与出弯点帧索引。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param start - start frame index (inclusive) / 起始帧索引（含）
 * @param end - end frame index (inclusive) / 结束帧索引（含）
 * @returns { entry, exit } frame indices (null when not found) /
 *          { entry, exit } 帧索引（未找到时为 null）
 */
export function findEntryExitPoints(
  frames: SkiTickData[],
  start: number,
  end: number
): { entry: number | null; exit: number | null } {
  const lo = Math.max(0, start);
  const hi = Math.min(frames.length - 1, end);
  if (lo > hi) return { entry: null, exit: null };

  const entryThreshold = AR3_PARAMS.entryThreshold; // 0.3
  const exitThreshold = AR3_PARAMS.exitThreshold;   // 0.15

  // Find entry: first frame where |gyro.z| > entryThreshold
  // 查找入弯点：首个 |gyro.z| > entryThreshold 的帧
  let entry: number | null = null;
  for (let i = lo; i <= hi; i++) {
    if (Math.abs(frames[i].gyroscope.z) > entryThreshold) {
      entry = i;
      break;
    }
  }
  if (entry === null) return { entry: null, exit: null };

  // Find exit: first frame after entry where |gyro.z| < exitThreshold
  // 查找出弯点：入弯之后首个 |gyro.z| < exitThreshold 的帧
  let exit: number | null = null;
  for (let i = entry + 1; i <= hi; i++) {
    if (Math.abs(frames[i].gyroscope.z) < exitThreshold) {
      exit = i;
      break;
    }
  }

  return { entry, exit };
}

// ===== Internal: Transfer Time =====

/**
 * Compute the pressure transfer time from entry to the first frame where
 * the ratio gets within transferCompleteDelta of the target ratio.
 *
 * Target ratio:
 * - gyro.z > 0 → 0.75 (right foot outside, ideal outside ratio)
 * - gyro.z < 0 → 0.25 (left foot outside; here 0.25 represents the
 *   outside-ratio target for the left-outside case per spec)
 *
 * Note: the spec defines the target as 0.75 when gyro.z > 0 and 0.25
 * when gyro.z < 0. We follow the spec literally.
 *
 * 计算压力转移时间：从入弯点起，到首个占比与目标值之差小于
 * transferCompleteDelta 的帧的时间差。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param entryIdx - entry frame index / 入弯帧索引
 * @param exitIdx - exit frame index (inclusive bound) / 出弯帧索引（含）
 * @returns transfer time in ms, or null if no completion found /
 *          转移时间（毫秒），未完成时为 null
 */
function computeTransferTime(
  frames: SkiTickData[],
  entryIdx: number,
  exitIdx: number
): number | null {
  const entryFrame = frames[entryIdx];
  const gyroZ = entryFrame.gyroscope.z;

  // Target ratio per spec: gyro.z > 0 → 0.75, gyro.z < 0 → 0.25
  // 规范目标值：gyro.z > 0 → 0.75，gyro.z < 0 → 0.25
  const target = gyroZ > 0 ? 0.75 : 0.25;
  const delta = AR3_PARAMS.transferCompleteDelta; // 0.1

  const lo = Math.max(0, entryIdx);
  const hi = Math.min(frames.length - 1, exitIdx);
  if (lo > hi) return null;

  for (let i = lo; i <= hi; i++) {
    const f = frames[i];
    const ratio = computeRatio(
      f.pressure_left,
      f.pressure_right,
      f.gyroscope.z
    );
    if (Math.abs(ratio - target) < delta) {
      // Transfer complete at frame i / 在第 i 帧完成转移
      return f.timestamp_ms - entryFrame.timestamp_ms;
    }
  }
  return null;
}

// ===== Internal: Score Components =====

/**
 * Ratio score (0-100): how close avg ratio is to 0.75.
 *   ratio_score = clamp(100 - |avg_ratio - 0.75| * 100, 0, 100)
 *
 * 占比得分（0-100）：平均占比与 0.75 的接近程度。
 */
function computeRatioScore(avgRatio: number): number {
  const ideal = AR3_PARAMS.idealOutsideRatio; // 0.75
  const score = 100 - Math.abs(avgRatio - ideal) * 100;
  return Math.max(0, Math.min(100, score));
}

/**
 * Fluctuation score (0-100) based on the std dev of the ratio.
 *   - fluctuation <= maxFluctuation (0.15) → 100
 *   - otherwise → max(0, 100 - (fluctuation - 0.15) * 200)
 *
 * 波动得分（0-100）：占比标准差越小越高。
 */
function computeFluctuationScore(fluctuation: number): number {
  const maxFluct = AR3_PARAMS.maxFluctuation; // 0.15
  if (fluctuation <= maxFluct) return 100;
  return Math.max(0, 100 - (fluctuation - maxFluct) * 200);
}

/**
 * Stability score (0-100): based on average absolute deviation of the
 * ratio from the ideal 0.75.
 *   stability_score = max(0, 100 * (1 - avg(|ratio - 0.75|) * 2))
 *
 * 稳定性得分（0-100）：占比偏离理想值 0.75 的平均绝对偏差。
 */
function computeStabilityScore(
  ratioSeries: number[],
  avgRatio: number
): number {
  // Spec: stability_score = 100 * (1 - avg(abs(ratio - 0.75)) * 2), floor 0
  // The avgRatio parameter is retained in the signature for callers that
  // wish to use a computed mean, but the formula uses the ideal 0.75.
  // 规范：stability = 100 * (1 - avg(|ratio - 0.75|) * 2)，下限 0
  const ideal = AR3_PARAMS.idealOutsideRatio; // 0.75
  void avgRatio; // not used per spec formula / 规范公式不使用 avgRatio

  if (ratioSeries.length === 0) return 0;

  let sumAbsDev = 0;
  for (const r of ratioSeries) {
    sumAbsDev += Math.abs(r - ideal);
  }
  const avgAbsDev = sumAbsDev / ratioSeries.length;
  const score = 100 * (1 - avgAbsDev * 2);
  return Math.max(0, score);
}

/**
 * Transfer speed score (0-100).
 *   speed_score = 1000 / max(avg_transfer_time_ms, 100)
 * Clamped to [0, 100]. When there is no transfer (transfer_time_ms === 0),
 * returns 0.
 *
 * 转移速度得分（0-100）。
 */
function computeTransferSpeedScore(transferTimeMs: number): number {
  if (transferTimeMs <= 0) return 0;
  const score = 1000 / Math.max(transferTimeMs, 100);
  return Math.max(0, Math.min(100, score));
}

// ===== Internal: Population Std Dev =====

/**
 * Compute the population standard deviation of a numeric series.
 * Returns 0 when fewer than 2 elements.
 *
 * 计算总体标准差。元素少于 2 时返回 0。
 */
function populationStdDev(values: number[]): number {
  const n = values.length;
  if (n < 2) return 0;

  let sum = 0;
  for (const v of values) sum += v;
  const mean = sum / n;

  let sqSum = 0;
  for (const v of values) {
    const d = v - mean;
    sqSum += d * d;
  }
  return Math.sqrt(sqSum / n);
}

// ===== Main Entry Point =====

/**
 * AR-3 Outside Pressure Index: evaluate pressure transfer quality for a
 * single turn.
 * Method ID: b1-pressure-index-comprehensive
 *
 * Algorithm steps / 算法步骤:
 * 1. Compute per-frame outside-foot pressure ratio over [start, end].
 *    计算 [start, end] 内每帧的外板压力占比。
 * 2. Derive peak / average ratio and fluctuation (population std dev).
 *    得到占比峰值、均值及波动（总体标准差）。
 * 3. Determine dominant outside foot across the turn.
 *    判定弯内主导外板。
 * 4. Locate entry/exit points and compute pressure transfer time.
 *    定位入弯/出弯点并计算压力转移时间。
 * 5. Compute sub-scores: ratio, fluctuation, stability, transfer speed.
 *    计算各项子分：占比、波动、稳定性、转移速度。
 * 6. Combine: score = ratio*0.35 + fluct*0.25 + stability*0.25 + speed*0.15
 *    综合：score = 占比*0.35 + 波动*0.25 + 稳定性*0.25 + 速度*0.15
 *
 * Confidence is fixed at AR3_PARAMS.confidence (0.85). When the frame
 * range has fewer than minDataPoints (10) frames, the score is still
 * computed but confidence is retained per the spec.
 *
 * Frame range [startFrameIndex, endFrameIndex] is inclusive on both ends.
 * 帧范围 [startFrameIndex, endFrameIndex] 两端均含。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param startFrameIndex - start frame index (inclusive) / 起始帧索引（含）
 * @param endFrameIndex - end frame index (inclusive) / 结束帧索引（含）
 * @returns OutsidePressureResult / 外侧压感指数结果
 */
export function computeOutsidePressure(
  frames: SkiTickData[],
  startFrameIndex: number,
  endFrameIndex: number
): OutsidePressureResult {
  const lo = Math.max(0, startFrameIndex);
  const hi = Math.min(frames.length - 1, endFrameIndex);

  // Edge case: empty or invalid range / 边界情况：空或无效范围
  if (lo > hi) {
    return {
      score: 0,
      confidence: AR3_PARAMS.confidence,
      outside_ratio_peak: 0,
      outside_ratio_avg: 0,
      outside_ratio_series: [],
      fluctuation: 0,
      stability_score: 0,
      ratio_score: 0,
      fluctuation_score: 100,
      transfer_speed_score: 0,
      transfer_time_ms: 0,
      outside_foot: 'neutral',
      entry_ms: null,
      exit_ms: null,
    };
  }

  // Step 1: per-frame outside-foot ratio / 步骤 1：每帧外板占比
  const ratioSeries: number[] = [];
  for (let i = lo; i <= hi; i++) {
    const f = frames[i];
    ratioSeries.push(
      computeRatio(f.pressure_left, f.pressure_right, f.gyroscope.z)
    );
  }

  // Step 2: peak / average / fluctuation / 步骤 2：峰值/均值/波动
  let peak = 0;
  let sum = 0;
  for (const r of ratioSeries) {
    if (r > peak) peak = r;
    sum += r;
  }
  const avgRatio = ratioSeries.length > 0 ? sum / ratioSeries.length : 0;
  const fluctuation = populationStdDev(ratioSeries);

  // Step 3: dominant outside foot / 步骤 3：主导外板
  // Use the frame with the largest |gyro.z| as the representative frame
  // for determining the outside foot of the whole turn.
  // 以 |gyro.z| 最大的帧作为整弯外板判定的代表帧。
  let dominantGyroZ = 0;
  for (let i = lo; i <= hi; i++) {
    const gz = frames[i].gyroscope.z;
    if (Math.abs(gz) > Math.abs(dominantGyroZ)) {
      dominantGyroZ = gz;
    }
  }
  const outsideFoot = determineOutsideFoot(dominantGyroZ);

  // Step 4: entry / exit + transfer time / 步骤 4：入弯/出弯 + 转移时间
  const { entry, exit } = findEntryExitPoints(frames, lo, hi);
  let transferTimeMs = 0;
  if (entry !== null) {
    // Search for transfer completion from entry to exit (or end if no exit)
    // 在 entry 到 exit（若无 exit 则到 hi）之间查找转移完成
    const searchEnd = exit !== null ? exit : hi;
    const t = computeTransferTime(frames, entry, searchEnd);
    if (t !== null) transferTimeMs = t;
  }

  // Step 5: sub-scores / 步骤 5：子分
  const ratioScore = computeRatioScore(avgRatio);
  const fluctuationScore = computeFluctuationScore(fluctuation);
  const stabilityScore = computeStabilityScore(ratioSeries, avgRatio);
  const transferSpeedScore = computeTransferSpeedScore(transferTimeMs);

  // Step 6: weighted total / 步骤 6：加权总分
  // score = ratio*0.35 + fluct*0.25 + stability*0.25 + speed*0.15
  const score =
    ratioScore * 0.35 +
    fluctuationScore * 0.25 +
    stabilityScore * 0.25 +
    transferSpeedScore * 0.15;
  const finalScore = Math.max(0, Math.min(100, score));

  // Resolve entry/exit timestamps (null when not found)
  // 解析入弯/出弯时间戳（未找到时为 null）
  const entryMs = entry !== null ? frames[entry].timestamp_ms : null;
  const exitMs = exit !== null ? frames[exit].timestamp_ms : null;

  return {
    score: finalScore,
    confidence: AR3_PARAMS.confidence,
    outside_ratio_peak: peak,
    outside_ratio_avg: avgRatio,
    outside_ratio_series: ratioSeries,
    fluctuation,
    stability_score: stabilityScore,
    ratio_score: ratioScore,
    fluctuation_score: fluctuationScore,
    transfer_speed_score: transferSpeedScore,
    transfer_time_ms: transferTimeMs,
    outside_foot: outsideFoot,
    entry_ms: entryMs,
    exit_ms: exitMs,
  };
}
